/**
 * Currency formatting utilities.
 *
 * Centralises the symbol lookup and number formatting that was previously
 * duplicated (and sometimes hard-coded) across multiple pages.
 */

const CURRENCY_SYMBOLS = {
  INR: '₹',
  MYR: 'RM',
  THB: '฿',
  IDR: 'Rp',
  SGD: 'S$',
  PHP: '₱',
  USD: '$',
};

// Currencies where a space separates the symbol from the number
const SPACED_CURRENCIES = new Set(['IDR', 'PHP']);

/**
 * Get the display symbol for a currency code.
 * Falls back to '$' if code is unknown or undefined.
 */
export function getCurrencySymbol(currencyCode) {
  return CURRENCY_SYMBOLS[currencyCode] || '$';
}

/**
 * Format a numeric value with a currency symbol.
 *
 * @param {number}  value            The numeric value to format
 * @param {object}  [opts]           Options
 * @param {string}  [opts.currency]  Currency code (e.g. 'MYR'). Determines symbol and spacing.
 * @param {string}  [opts.symbol]    Direct symbol override. Takes precedence over currency lookup.
 * @param {number}  [opts.decimals]  Decimal places. Defaults to 0 for IDR, 2 for others.
 * @param {boolean} [opts.compact]   Format large numbers as '1.2k' / '1.5M'. Default false.
 * @param {boolean} [opts.showSign]  Prepend '+' for positive values (P&L). Default false.
 * @returns {string} e.g. "RM1,234.00", "Rp 85,000", "+₹500.00"
 */
export function fmtCurrency(value, opts = {}) {
  const currency = opts.currency || 'USD';
  const sym      = opts.symbol || getCurrencySymbol(currency);
  const space    = SPACED_CURRENCIES.has(currency) ? ' ' : '';

  let decimals = opts.decimals;
  if (decimals === undefined) {
    decimals = currency === 'IDR' ? 0 : 2;
  }

  const sign = opts.showSign && value > 0 ? '+' : '';

  if (opts.compact) {
    let display;
    const abs = Math.abs(value);
    if (abs >= 1_000_000) {
      display = (value / 1_000_000).toFixed(1) + 'M';
    } else if (abs >= 1_000) {
      display = (value / 1_000).toFixed(1) + 'k';
    } else {
      display = value.toLocaleString('en', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    }
    return `${sign}${sym}${space}${display}`;
  }

  const formatted = value.toLocaleString('en', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return `${sign}${sym}${space}${formatted}`;
}
